import { devLog, devError } from '../utils/devLogger';
import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Modal, Form, Alert, Spinner, Tab, Tabs } from 'react-bootstrap';
import { useAuth } from '../auth/AuthContext';
import { db } from '../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import AdminNavbar from '../components/AdminNavbar';
import '../styles/brand.css';

const Messages = () => {
  const { currentUser, userProfile } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingMessage, setEditingMessage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    type: 'announcement',
    priority: 'normal',
    targetAudience: 'all',
    status: 'draft',
    expiresAt: ''
  });
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    if (currentUser && userProfile?.role === 'admin') {
      loadMessages();
    }
  }, [currentUser, userProfile]);

  const loadMessages = async () => {
    try {
      setLoading(true);
      
      const messagesCollection = collection(db, 'notifications');
      const messagesQuery = query(messagesCollection, orderBy('createdAt', 'desc'));
      const messagesSnapshot = await getDocs(messagesQuery);
      const messagesData = messagesSnapshot.docs
        .map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            title: data.title || data.subject || 'Untitled message',
            content: data.content || data.message || data.body || '',
            type: data.type || 'announcement',
            priority: data.priority || 'normal',
            targetAudience: data.targetAudience || 'all',
            status: data.status || 'published',
            createdAt: data.createdAt?.toDate?.() || new Date(data.createdAt || Date.now()),
            expiresAt: data.expiresAt?.toDate?.() || null
          };
        });
      
      setMessages(messagesData);
      
    } catch (error) {
      devError('Error loading messages:', error);
      showAlert('Error loading messages', 'danger');
    } finally {
      setLoading(false);
    }
  };

  const showAlert = (message, type) => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: '', type: '' }), 5000);
  };

  const handleShowModal = (message = null) => {
    if (message) {
      setEditingMessage(message);
      setFormData({
        title: message.title,
        content: message.content,
        type: message.type || 'announcement',
        priority: message.priority || 'normal',
        targetAudience: message.targetAudience || 'all',
        status: message.status || 'draft',
        expiresAt: message.expiresAt ? message.expiresAt.toISOString().split('T')[0] : ''
      });
    } else {
      setEditingMessage(null);
      setFormData({
        title: '',
        content: '',
        type: 'announcement',
        priority: 'normal',
        targetAudience: 'all',
        status: 'draft',
        expiresAt: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingMessage(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const messageData = {
        ...formData,
        expiresAt: formData.expiresAt ? new Date(formData.expiresAt) : null,
        updatedAt: serverTimestamp()
      };

      if (editingMessage) {
        await updateDoc(doc(db, 'notifications', editingMessage.id), messageData);
        showAlert('Message updated successfully!', 'success');
      } else {
        await addDoc(collection(db, 'notifications'), {
          ...messageData,
          createdAt: serverTimestamp(),
          createdBy: currentUser.uid,
          userId: currentUser.uid,
          readBy: [],
          readCount: 0
        });
        showAlert('Message created successfully!', 'success');
      }
      
      handleCloseModal();
      loadMessages();
      
    } catch (error) {
      devError('Error saving message:', error);
      showAlert('Error saving message', 'danger');
    }
  };

  const handleDelete = async (messageId) => {
    if (window.confirm('Are you sure you want to delete this message? This action cannot be undone.')) {
      try {
        await deleteDoc(doc(db, 'notifications', messageId));
        showAlert('Message deleted successfully!', 'success');
        loadMessages();
      } catch (error) {
        devError('Error deleting message:', error);
        showAlert('Error deleting message', 'danger');
      }
    }
  };

  const handlePublish = async (messageId) => {
    try {
      await updateDoc(doc(db, 'notifications', messageId), {
        status: 'published',
        publishedAt: serverTimestamp()
      });
      showAlert('Message published successfully!', 'success');
      loadMessages();
    } catch (error) {
      devError('Error publishing message:', error);
      showAlert('Error publishing message', 'danger');
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      'draft': 'secondary',
      'published': 'success',
      'archived': 'dark',
      'expired': 'warning'
    };
    return <Badge bg={variants[status] || 'secondary'}>{status}</Badge>;
  };

  const getTypeBadge = (type) => {
    const variants = {
      'announcement': 'primary',
      'maintenance': 'warning',
      'urgent': 'danger',
      'info': 'info'
    };
    return <Badge bg={variants[type] || 'primary'}>{type}</Badge>;
  };

  const getPriorityBadge = (priority) => {
    const variants = {
      'low': 'success',
      'normal': 'info',
      'high': 'warning',
      'urgent': 'danger'
    };
    return <Badge bg={variants[priority] || 'info'}>{priority}</Badge>;
  };

  const isExpired = (expiresAt) => {
    return expiresAt && new Date() > expiresAt;
  };

  if (userProfile?.role !== 'admin') {
    return (
      <Container className="mt-5">
        <Alert variant="danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access message management.</p>
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <>
        <AdminNavbar currentPage="messages" />
        <Container className="mt-4">
          <div className="text-center my-5">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
            <p className="mt-2">Loading messages...</p>
          </div>
        </Container>
      </>
    );
  }

  const publishedMessages = messages.filter(m => m.status === 'published');
  const draftMessages = messages.filter(m => m.status === 'draft');
  const expiredMessages = messages.filter(m => isExpired(m.expiresAt));

  return (
    <>
      <AdminNavbar currentPage="messages" />
      
      {/* Hero Section */}
      <section className="hero-section">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="mb-3">Message Management</h1>
              <p className="mission-text mb-4">
                Manage system messages and communications
              </p>
              <p className="mb-0">
                Create, manage, and distribute system-wide messages and announcements to keep users informed about important updates, maintenance notices, and organizational communications.
              </p>
            </Col>
            <Col lg={4} className="d-flex align-items-center justify-content-end">
              <Button variant="outline-light" onClick={() => handleShowModal()}>
                Create New Message
              </Button>
            </Col>
          </Row>
        </Container>
      </section>

      <Container className="mt-4">

        {alert.show && (
          <Alert variant={alert.type} className="mb-4">
            {alert.message}
          </Alert>
        )}

        {/* Message Statistics */}
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-primary">{messages.length}</h3>
                <small className="text-muted">Total Messages</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-success">{publishedMessages.length}</h3>
                <small className="text-muted">Published</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-warning">{draftMessages.length}</h3>
                <small className="text-muted">Drafts</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <h3 className="text-danger">{expiredMessages.length}</h3>
                <small className="text-muted">Expired</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Messages Tabs */}
        <Card>
          <Card.Header>
            <h5>All Messages</h5>
          </Card.Header>
          <Card.Body>
            <Tabs defaultActiveKey="all" className="mb-3">
              <Tab eventKey="all" title="All Messages">
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Message</th>
                      <th>Type</th>
                      <th>Priority</th>
                      <th>Audience</th>
                      <th>Status</th>
                      <th>Created</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {messages.map((message) => (
                      <tr key={message.id} className={isExpired(message.expiresAt) ? 'table-warning' : ''}>
                        <td>
                          <strong>{message.title}</strong>
                          <div><small className="text-muted">{message.content.substring(0, 100)}...</small></div>
                        </td>
                        <td>{getTypeBadge(message.type)}</td>
                        <td>{getPriorityBadge(message.priority)}</td>
                        <td>
                          <Badge bg="info">{message.targetAudience}</Badge>
                        </td>
                        <td>{getStatusBadge(isExpired(message.expiresAt) ? 'expired' : message.status)}</td>
                        <td>
                          <small>{message.createdAt.toLocaleDateString()}</small>
                        </td>
                        <td>
                          {message.expiresAt ? (
                            <small className={isExpired(message.expiresAt) ? 'text-danger' : ''}>
                              {message.expiresAt.toLocaleDateString()}
                            </small>
                          ) : (
                            <small className="text-muted">Never</small>
                          )}
                        </td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-1"
                            onClick={() => handleShowModal(message)}
                          >
                            Edit
                          </Button>
                          {message.status === 'draft' && (
                            <Button 
                              variant="outline-success" 
                              size="sm"
                              className="me-1"
                              onClick={() => handlePublish(message.id)}
                            >
                              Publish
                            </Button>
                          )}
                          <Button 
                            variant="outline-danger" 
                            size="sm"
                            onClick={() => handleDelete(message.id)}
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {messages.length === 0 && (
                      <tr>
                        <td colSpan="8" className="text-center text-muted py-4">
                          No messages found. Create your first message to get started.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </Tab>
              
              <Tab eventKey="published" title={`Published (${publishedMessages.length})`}>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Message</th>
                      <th>Type</th>
                      <th>Priority</th>
                      <th>Published</th>
                      <th>Expires</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {publishedMessages.map((message) => (
                      <tr key={message.id}>
                        <td>
                          <strong>{message.title}</strong>
                          <div><small className="text-muted">{message.content.substring(0, 100)}...</small></div>
                        </td>
                        <td>{getTypeBadge(message.type)}</td>
                        <td>{getPriorityBadge(message.priority)}</td>
                        <td>
                          <small>{message.publishedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}</small>
                        </td>
                        <td>
                          {message.expiresAt ? (
                            <small className={isExpired(message.expiresAt) ? 'text-danger' : ''}>
                              {message.expiresAt.toLocaleDateString()}
                            </small>
                          ) : (
                            <small className="text-muted">Never</small>
                          )}
                        </td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            onClick={() => handleShowModal(message)}
                          >
                            Edit
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Tab>
              
              <Tab eventKey="drafts" title={`Drafts (${draftMessages.length})`}>
                <Table striped bordered hover responsive>
                  <thead>
                    <tr>
                      <th>Message</th>
                      <th>Type</th>
                      <th>Priority</th>
                      <th>Created</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {draftMessages.map((message) => (
                      <tr key={message.id}>
                        <td>
                          <strong>{message.title}</strong>
                          <div><small className="text-muted">{message.content.substring(0, 100)}...</small></div>
                        </td>
                        <td>{getTypeBadge(message.type)}</td>
                        <td>{getPriorityBadge(message.priority)}</td>
                        <td>
                          <small>{message.createdAt.toLocaleDateString()}</small>
                        </td>
                        <td>
                          <Button 
                            variant="outline-primary" 
                            size="sm" 
                            className="me-1"
                            onClick={() => handleShowModal(message)}
                          >
                            Edit
                          </Button>
                          <Button 
                            variant="outline-success" 
                            size="sm"
                            onClick={() => handlePublish(message.id)}
                          >
                            Publish
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Tab>
            </Tabs>
          </Card.Body>
        </Card>

        {/* Add/Edit Message Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>
              {editingMessage ? 'Edit Message' : 'Create New Message'}
            </Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              <Form.Group className="mb-3">
                <Form.Label>Message Title *</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., System Maintenance Notice"
                />
              </Form.Group>
              
              <Form.Group className="mb-3">
                <Form.Label>Message Content *</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={5}
                  name="content"
                  value={formData.content}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter the full message content here..."
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Message Type *</Form.Label>
                    <Form.Select
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="announcement">Announcement</option>
                      <option value="maintenance">Maintenance</option>
                      <option value="urgent">Urgent</option>
                      <option value="info">Information</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Priority *</Form.Label>
                    <Form.Select
                      name="priority"
                      value={formData.priority}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Target Audience *</Form.Label>
                    <Form.Select
                      name="targetAudience"
                      value={formData.targetAudience}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="all">All Users</option>
                      <option value="learners">Learners Only</option>
                      <option value="facilitators">Facilitators Only</option>
                      <option value="admins">Admins Only</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Status *</Form.Label>
                    <Form.Select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      required
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>Expiration Date (Optional)</Form.Label>
                <Form.Control
                  type="date"
                  name="expiresAt"
                  value={formData.expiresAt}
                  onChange={handleInputChange}
                />
                <Form.Text className="text-muted">
                  Leave blank if the message should never expire
                </Form.Text>
              </Form.Group>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal}>
                Cancel
              </Button>
              <Button variant="primary" type="submit">
                {editingMessage ? 'Update Message' : 'Create Message'}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </>
  );
};

export default Messages;
